import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import apiFiles from 'api/files';
import apiOffers from 'api/offers';
import IOfferProductImage from 'api/interfaces/offers/IOfferProductImage';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import {
  CONTENT_WIDTH_MM,
  DEFAULT_IMAGE_HEIGHT,
  IMAGE_HEIGHTS,
  getImageHeightMm,
} from 'services/enums/offers/imageHeights';
import { finishLoading, startLoading } from 'store/loading/actions';
import { addMessage } from 'store/messages/actions';

import 'style/offers/OfferProductImages.css';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];
const MAX_CAPTION_LENGTH = 300;

// An area with the same proportions as the image area in the offer PDF, so the user sees
// how the image fills the chosen area on the offer
const ImageFrame = ({
  children,
  height,
}: {
  children: React.ReactNode;
  height: string;
}): JSX.Element => (
  <div
    className="offer-product-image-frame"
    style={{
      paddingTop: `${(getImageHeightMm(height) / CONTENT_WIDTH_MM) * 100}%`,
    }}
  >
    <div className="offer-product-image-frame-content">{children}</div>
  </div>
);

/**
 * Images of one alternative (calculation version) of a position. In the offer PDF they are
 * printed after the text of that alternative, each with its caption below it.
 *
 * Without images there is one empty image area to drop or choose an image. The added image
 * takes the place of the empty area. "Add new image" adds an empty area below the images.
 */
const OfferProductImages = ({
  locked,
  offerProductId,
  versionId,
}: {
  locked: boolean;
  offerProductId: number;
  versionId: number;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);
  const fileInput = useRef<HTMLInputElement>(null);

  const [images, setImages] = useState<IOfferProductImage[] | null>(null);
  const [imageUrls, setImageUrls] = useState<{ [imageId: number]: string }>({});
  const [imageToDelete, setImageToDelete] = useState<IOfferProductImage>();
  // Captions being edited, saved when the field loses focus
  const [captions, setCaptions] = useState<{ [imageId: number]: string }>({});
  // The empty image area for a new image
  const [addingNew, setAddingNew] = useState(false);
  const [newImageHeight, setNewImageHeight] = useState(DEFAULT_IMAGE_HEIGHT);
  const [newImageCaption, setNewImageCaption] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const loadImages = async () => {
    try {
      const loaded = await apiOffers.products.images.get(
        offerProductId,
        versionId
      );
      setImages(loaded);
      setCaptions(
        loaded.reduce(
          (all, image) => ({ ...all, [image.id]: image.caption ?? '' }),
          {}
        )
      );
    } catch (error) {
      setImages([]);
    }
  };

  useEffect(() => {
    loadImages();
  }, [offerProductId, versionId]);

  // The image files require authorization, so they are fetched as blobs
  useEffect(() => {
    let cancelled = false;
    const missingImages = (images ?? []).filter(
      (image) => !imageUrls[image.id]
    );

    if (missingImages.length === 0) {
      return undefined;
    }

    const fetchImages = async () => {
      const fetched: { [imageId: number]: string } = {};

      await Promise.all(
        missingImages.map(async (image) => {
          try {
            const response = await apiOffers.products.images.getFile(
              offerProductId,
              image.id
            );
            const blob = new Blob([response.data], {
              type: response.headers['content-type'],
            });
            fetched[image.id] = URL.createObjectURL(blob);
          } catch (error) {
            // The area is shown empty if the image cannot be loaded
          }
        })
      );

      if (!cancelled) {
        setImageUrls((current) => ({ ...current, ...fetched }));
      }
    };

    fetchImages();

    return () => {
      cancelled = true;
    };
  }, [images]);

  useEffect(
    () => () => {
      Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url));
    },
    []
  );

  const hasImages = images !== null && images.length > 0;
  const showNewImageArea = !locked && (!hasImages || addingNew);

  // Adds the chosen or dropped images in order with the height and caption of the area.
  // When several images are dropped, the caption goes to the first one.
  const addImages = async (files: File[]) => {
    const imageFiles = files.filter((file) =>
      ACCEPTED_TYPES.includes(file.type)
    );

    if (imageFiles.length < files.length) {
      dispatch(addMessage(t(`${translations.offers}:Image type not allowed`)));
    }

    if (imageFiles.length === 0) {
      return;
    }

    dispatch(startLoading('offer-product-image'));

    try {
      const caption = newImageCaption.trim();

      // eslint-disable-next-line no-restricted-syntax
      for (const [index, file] of imageFiles.entries()) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await apiFiles.post(file, null);
        // eslint-disable-next-line no-await-in-loop
        await apiOffers.products.images.post(
          offerProductId,
          versionId,
          `${uploaded.id}`,
          newImageHeight,
          index === 0 && caption !== '' ? caption : null
        );
      }

      setAddingNew(false);
      setNewImageCaption('');
      dispatch(addMessage(t(`${translations.offers}:Image added`)));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Image failed`)));
    } finally {
      await loadImages();
      dispatch(finishLoading('offer-product-image'));
    }
  };

  const addSelectedImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    addImages(files);
  };

  const isFileDrag = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types).includes('Files');

  const onDragOver = (event: React.DragEvent) => {
    if (!isFileDrag(event)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragActive(true);
  };

  const onDragLeave = (event: React.DragEvent) => {
    // Leaving to a child element of the drop area is not leaving the area
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setDragActive(false);
    }
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    addImages(Array.from(event.dataTransfer.files));
  };

  const updateImage = async (
    image: IOfferProductImage,
    height: string,
    caption: null | string
  ) => {
    try {
      await apiOffers.products.images.patch(
        offerProductId,
        image.id,
        height,
        caption
      );
      setImages(
        (images ?? []).map((existing) =>
          existing.id === image.id ? { ...existing, caption, height } : existing
        )
      );
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Image failed`)));
    }
  };

  const saveCaption = (image: IOfferProductImage) => {
    const caption = (captions[image.id] ?? '').trim();

    if (caption !== (image.caption ?? '')) {
      updateImage(image, image.height, caption === '' ? null : caption);
    }
  };

  const deleteImage = async () => {
    const image = imageToDelete;
    setImageToDelete(undefined);

    if (image === undefined) {
      return;
    }

    try {
      await apiOffers.products.images.delete(offerProductId, image.id);
      setImages((images ?? []).filter((existing) => existing.id !== image.id));
    } catch (error) {
      dispatch(addMessage(t(`${translations.offers}:Image failed`)));
    }
  };

  const heightSelect = (
    id: string,
    value: string,
    onChange: (height: string) => void
  ) => (
    <Input
      aria-label={t(`${translations.offers}:Image height`)}
      className="offer-product-image-height"
      disabled={locked}
      id={id}
      onChange={(event) => onChange(event.target.value)}
      type="select"
      value={value}
    >
      {IMAGE_HEIGHTS.map((option) => (
        <option key={option.value} value={option.value}>
          {t(`${translations.offers}:imageHeights.${option.value}`)}
        </option>
      ))}
    </Input>
  );

  return (
    <div className="offer-product-images">
      <ModalConfirm
        showing={imageToDelete !== undefined}
        setShowing={() => setImageToDelete(undefined)}
        header={t(`${translations.offers}:Delete image`)}
        text={t(`${translations.offers}:Delete image confirmation`)}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={deleteImage}
      />
      <p className="label offer-product-images-title">
        {t(`${translations.offers}:Images`)}
      </p>

      {(images ?? []).map((image) => (
        <div key={image.id} className="offer-product-image-card">
          <ImageFrame height={image.height}>
            {imageUrls[image.id] === undefined ? null : (
              <img src={imageUrls[image.id]} alt={image.name} />
            )}
          </ImageFrame>
          <div className="offer-product-image-row">
            <Input
              aria-label={t(`${translations.offers}:Image caption`)}
              className="offer-product-image-caption"
              disabled={locked}
              maxLength={MAX_CAPTION_LENGTH}
              onBlur={() => saveCaption(image)}
              onChange={(event) =>
                setCaptions({ ...captions, [image.id]: event.target.value })
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.currentTarget.blur();
                }
              }}
              placeholder={t(`${translations.offers}:Image caption`)}
              type="text"
              value={captions[image.id] ?? ''}
            />
            {heightSelect(`image-height-${image.id}`, image.height, (height) =>
              updateImage(image, height, image.caption)
            )}
            {locked ? null : (
              <button
                aria-label={t(`${translations.offers}:Delete image`)}
                className="offer-product-image-delete"
                onClick={() => setImageToDelete(image)}
                title={t(`${translations.offers}:Delete image`)}
                type="button"
              >
                &#10005;
              </button>
            )}
          </div>
        </div>
      ))}

      {showNewImageArea && images !== null ? (
        <div className="offer-product-image-card new-image">
          <div
            className={`offer-product-image-drop-zone${
              dragActive ? ' drag-active' : ''
            }`}
            onClick={() => fileInput.current?.click()}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                fileInput.current?.click();
              }
            }}
            role="button"
            tabIndex={0}
          >
            <ImageFrame height={newImageHeight}>
              <span className="offer-product-image-drop-text">
                <strong>+ {t(`${translations.offers}:Add image`)}</strong>
                <br />
                {t(`${translations.offers}:Drop images here`)}
              </span>
            </ImageFrame>
          </div>
          <div className="offer-product-image-row">
            <Input
              aria-label={t(`${translations.offers}:Image caption`)}
              className="offer-product-image-caption"
              maxLength={MAX_CAPTION_LENGTH}
              onChange={(event) => setNewImageCaption(event.target.value)}
              placeholder={t(`${translations.offers}:Image caption`)}
              type="text"
              value={newImageCaption}
            />
            {heightSelect(
              'new-image-height',
              newImageHeight,
              setNewImageHeight
            )}
            {hasImages ? (
              <button
                aria-label={t(`${translations.common}:Cancel`)}
                className="offer-product-image-delete"
                onClick={() => setAddingNew(false)}
                title={t(`${translations.common}:Cancel`)}
                type="button"
              >
                &#10005;
              </button>
            ) : null}
          </div>
          <input
            accept={ACCEPTED_TYPES.join(',')}
            hidden
            multiple
            onChange={addSelectedImages}
            ref={fileInput}
            type="file"
          />
        </div>
      ) : null}

      {!locked && hasImages && !addingNew ? (
        <PrimaryButton
          className="button-one-line outline-button"
          onClick={() => setAddingNew(true)}
        >
          + {t(`${translations.offers}:Add new image`).toLocaleUpperCase()}
        </PrimaryButton>
      ) : null}

      {locked && !hasImages ? (
        <p className="offer-product-images-info">
          {t(`${translations.offers}:No images`)}
        </p>
      ) : (
        <p className="offer-product-images-info">
          {t(`${translations.offers}:Images info`)}
        </p>
      )}
    </div>
  );
};

export default OfferProductImages;
